/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import React, { Fragment } from 'react'
import { message } from 'antd'
import { ListView } from 'antd-mobile'
import * as _ from 'lodash'
import { isMobile } from 'react-device-detect'
import PageLoading from '@components/PageLoading'
import useRootStore from '@store/useRootStore'
import { MessageRecordSource, MessageStore } from '@store/messageStore'
import { bettingHistoryStore } from '@store/index'
import { delay, showToast, TOAST_TYPE } from '@utils/utils'
import { Schema$Message } from '@v2/protocol/message'
import { AuthStore } from '@store/authStore'
import styled, { Theme } from '@themes/theme'
import {
  BETTING_HISTORY_TAB_KEY,
  EMPTY_ICON,
  MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY,
  MESSAGE_TYPE_TAB_INDEX_KEY,
} from '@constants/index'
import { AdvEmpty } from '@components/AdvEmpty'
import { GlobalStore } from '@store/globalStore'
import BettingHistory from '@views/BettingHistory'
import { getErrorText } from '@game/errtext'
import { ModalContents } from '@styles/base.style'
import { MESSAGE_DIR } from 'adv-protocol/src/GameCommon'
import { ComponentStore } from '@store/componentStore'
import MessageListItem from '../MessageListItem'
import AllDeletePopup from '../Popup/AllDeletePopup'
import { showReceiveFailItemsMessage, showReceiveItemErrorMessage } from '@views/Popup/Message/MessageHelper'
import { COMMUNICATION_BLOCK_TYPE, FROM_VIEW_TYPE } from '@v2/constants/common'
import { LogStore } from '@store/logStore'

const LoadingStyle = (theme: Theme) => css`
    display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  /* height: calc(100% - 55px); */
  background: ${theme.color.contentsBg};
`

function getEmptyPage(isLoading: boolean) {
  return <div css={LoadingStyle}>{isLoading ? <PageLoading/> : <Fragment/>}</div>
}

const MessageListWrap = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 0;
  .message-list {
    padding-bottom: 60px;
    .am-list-view-scrollview-content.am-list {
      position: relative !important;
    }
  }
  .am-list-footer {
    margin-top: 0;
  }
`

export interface MessageListRef {
  onClickDeleteAllButton: () => void
  onReceiveAllItems: () => void
  onClickReadAllMessage: () => void
}

interface MessageListProps {
  currentMessageTypeTabKey: MESSAGE_TYPE_TAB_INDEX_KEY
  currentSendReceiveTabKey: MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY
  dir: MESSAGE_DIR
  clubId?: string
  fetchMessageList?: (
    index: MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY,
    size: number,
    dir: number,
    clubId: string,
    isClubMessage: boolean,
    resetForce: boolean,
  ) => Promise<MessageRecordSource>
  messageStore: MessageStore
  componentStore: ComponentStore
  authStore: AuthStore
  globalStore: GlobalStore
  routerStore: RouterStore
  logStore: LogStore
  messageListRef: React.MutableRefObject<MessageListRef>
}

interface MessageListState {
  // antd-mobile의 ListView.DataSource 가 any로 설정되어 있어서 어쩔수 없이 any로 한다.
  dataSource: any
  isLoading: boolean
  hasMore: boolean
  visibleDelete: boolean
  disabled: boolean
}

const PAGE_SIZE = 20

// function getEmptyPage(isLoading: boolean) {
//   return <div css={EmptyStyle}>{isLoading ? <PageLoading /> : <Fragment />}</div>
// }

function MyBody(props: any) {
  const { children } = props
  return <div className="am-list-body my-body">{children}</div>
}

class MessageList extends React.PureComponent<MessageListProps, MessageListState> {
  private mounted = false

  private lastMessageTypeTabKey = null

  private lastIndex = null

  private lastClubId = null

  private rowData = []

  private messageTypeText

  constructor(props) {
    super(props)

    const dataSource = new ListView.DataSource({
      rowHasChanged: (row1, row2) => row1 !== row2,
    })

    this.state = {
      dataSource,
      isLoading: false,
      hasMore: true,
      visibleDelete: false,
      disabled: false,
    }
  }

  componentDidMount() {
    const { currentMessageTypeTabKey, currentSendReceiveTabKey, clubId } = this.props
    this.lastMessageTypeTabKey = currentMessageTypeTabKey
    this.lastIndex = currentSendReceiveTabKey
    this.lastClubId = clubId
    this.mounted = true
    this.initializeMessageListRef()
    this.fetch(true).catch(console.error)
  }

  initializeMessageListRef() {
    const { messageListRef } = this.props
    messageListRef.current.onClickDeleteAllButton = this.onClickDeleteAllButton
    messageListRef.current.onReceiveAllItems = this.onReceiveAllItems
    messageListRef.current.onClickReadAllMessage = this.onClickReadAllMessage
  }

  async fetch(reset = false) {
    const { dataSource, isLoading, hasMore } = this.state
    const { currentMessageTypeTabKey, currentSendReceiveTabKey, dir, fetchMessageList, clubId } = this.props

    if (reset === true) {
      const newSource = dataSource.cloneWithRows([])
      this.rowData = []
      this.setState({
        dataSource: newSource,
        isLoading: true,
        hasMore: true,
      })
    } else {
      if (isLoading || !hasMore) {
        return
      }

      this.setState({ isLoading: true })
    }

    const isClubMessage = currentMessageTypeTabKey === MESSAGE_TYPE_TAB_INDEX_KEY.CLUB_MESSAGE
    const fetched: MessageRecordSource = await fetchMessageList(
      currentSendReceiveTabKey, PAGE_SIZE, dir, clubId, isClubMessage, reset
    )

    if (fetched) {
      this.rowData = _.concat(this.rowData, fetched.lastFetched || [])
      const rows = { ...this.rowData }
      const newSource = dataSource.cloneWithRows(rows)

      // TEST: loading test
      await delay(250)

      this.setState({
        isLoading: false,
        dataSource: newSource,
        hasMore: fetched.hasMore,
      })
    } else {
      this.setState({
        isLoading: false,
        dataSource: dataSource.cloneWithRows([]),
        hasMore: false,
      })
    }
  }

  onEndReached = async () => {
    await this.fetch()
  }

  onClickReadAllMessage = async () => {
    const { messageStore, clubId } = this.props
    messageStore.setReadableCount(0)

    const params: any = {}
    params.all = true
    if (clubId) {
      params.clubId = clubId
    }

    // const selectClubId: string = globalStore.getClubIdFromUrl()

    await messageStore.readMessage(params)
    for (const data of this.rowData) {
      if (data.receiverRead === 0) {
        data.receiverRead = 1
      }
    }
    this.onRefreshView().catch(console.error)
  }

  setVisibleDelete = (visible: boolean) => {
    this.setState({
      visibleDelete: visible,
    })
  }

  onRefreshView = async () => {
    const newSource = new ListView.DataSource({
      rowHasChanged: (row1, row2) => row1 !== row2,
    })
    this.setState({
      dataSource: newSource.cloneWithRows({ ...this.rowData }),
    })
  }

  onDeleteMessage = async (messageId?: string) => {
    const { dir, messageStore } = this.props
    const { messageList } = messageStore
    if (messageId) {
      const row = _.find(this.rowData, { id: messageId })
      if (dir === MESSAGE_DIR.RECV && row.itemCode > 0 && row.receivedItem === 0) {
        showToast(TOAST_TYPE.INFO, `${this.messageTypeText} 아이템을 받은 후 메시지 삭제가 가능합니다.`)
      } else {
        try {
          await messageStore.deleteMessage({ messageId })
        } catch (err) {
          showToast(TOAST_TYPE.INFO, `${this.messageTypeText} 삭제 실패!  잠시후 다시 시도해 주세요.`)
        }
        _.remove(this.rowData, { id: messageId })
        messageList.total -= 1
        this.onRefreshView().catch(console.error)
        showToast(TOAST_TYPE.INFO, `${this.messageTypeText}를 삭제 하였습니다.`)
      }
    } else {
      message.error(`선택된 ${this.messageTypeText}가 없습니다.`)
    }
  }

  onDeleteAllMessage = async () => {
    const { dir, messageStore, clubId } = this.props
    const { receivedItemCount } = messageStore
    if (_.isNumber(dir)) {
      if (dir === MESSAGE_DIR.RECV && receivedItemCount > 0) {
        showToast(TOAST_TYPE.INFO, `아이템을 모두 받은 후 ${this.messageTypeText} 삭제가 가능합니다.`)
        this.setVisibleDelete(false)
      } else {
        try {
          await messageStore.deleteMessage({ dir, all: true, clubId })
        } catch (err) {
          showToast(TOAST_TYPE.INFO, `${this.messageTypeText} 삭제 실패!  잠시후 다시 시도해 주세요.`)
        }
        this.rowData = []
        messageStore.setTotalCount(0)
        if (dir === MESSAGE_DIR.RECV) {   // 받은 메시지
          messageStore.setTotalReceiveMessageCount(0)
        } else {
          messageStore.setTotalSendMessageCount(0)
        }
        messageStore.setReadableCount(0)
        this.setVisibleDelete(false)
        this.onRefreshView().catch(console.error)
        showToast(TOAST_TYPE.INFO, `${this.messageTypeText}를 모두 삭제 하였습니다.`)
      }
    } else {
      message.error(`${this.messageTypeText}를 삭제 할 수 없습니다.`)
    }
  }

  onReadMessage = async (messageId?: string) => {
    const { messageStore } = this.props
    const { readableCount } = messageStore
    const row = _.find(this.rowData, { id: messageId })
    if (row.receiverRead === 0) {
      await messageStore.readMessage({ messageId })
      row.receiverRead = 1
      messageStore.setReadableCount(readableCount - 1)
    }
    this.onRefreshView().catch(console.error)
  }

  onReceiveItem = async (messageId?: string) => {
    const { messageStore, authStore } = this.props
    const { receivedItemCount } = messageStore
    const row = _.find(this.rowData, { id: messageId })
    if (row.receivedItem === 0 && row.itemCode > 0) {
      const { receiveFailMessageDetail, errcode } = await messageStore.receiveMessage({ messageId })
      const { failItems } = receiveFailMessageDetail
      if (!_.isEmpty(failItems)) {
        showReceiveFailItemsMessage(failItems)
      } else if (errcode) {
        showReceiveItemErrorMessage(errcode)
      } else {
        row.receivedItem = 1
        row.receiverRead = 1
        messageStore.setReceivedItemCount(receivedItemCount - 1)

        message.info(this.makeReceiveItemMessage(row))
        await authStore.refreshSimpleUserInfo(true)
      }
    }
    this.onRefreshView().catch(console.error)
  }

  showSaveBettingHistoryCompletePopup = () => {
    const { globalStore, routerStore, logStore } = this.props

    globalStore.pushDialogYesNo({
      title: '저장 완료',
      text: ['저장 내역과 동일한 경기에', '베팅이 가능합니다.', '이동하시겠습니까?'],
      className: 'small-size',
      negativePress: '취소',
      positivePress: '이동',
      callbackPositive: () => {
        globalStore.setVisibleMessageBox(false)
        globalStore.setBettingHistoryTabInitialTabKey(BETTING_HISTORY_TAB_KEY.SHARED)
        bettingHistoryStore.resetSelectedBettingHistory(FROM_VIEW_TYPE.MESSAGE)
        bettingHistoryStore.setBettingHistoryInitProps({ onlyMajorClub: false, fromViewType: FROM_VIEW_TYPE.MESSAGE })
        if (isMobile) {
          routerStore.push('/betting-history')
        } else {
          globalStore.addGeneralPopupProperties(<BettingHistory/>, '베팅 내역', {
            wrapClassName: '', className: '', isScroll: false, custom: false, clsBtn: false,
            closeCallback: () => {
              logStore.writeButtonLog({ pageId: 'c010', buttonId: '002' }).catch(console.error)
            },
          })
        }
      },
    })
  }

  onSaveBettingHistoryShare = async (messageId: string) => {
    const { messageStore } = this.props

    const row = _.find(this.rowData, { id: messageId })
    try {
      const result = await bettingHistoryStore.saveBettingHistory(row.id, row.sharedBettingHistory.betId, row.senderId)
      if (result?.errcode) {
        const errcode = result.errcode
        const errText = getErrorText(errcode) || '서버 연결이 원활하지 않습니다. 새로고침해 주세요.'
        showToast(TOAST_TYPE.INFO, errText)
      } else {
        await messageStore.readMessage({ messageId })
        this.showSaveBettingHistoryCompletePopup()
        row.receivedItem = 1
        row.receiverRead = 1
      }
    } catch (err) {
      console.dir(err)
      message.info('베팅 내역 저장 중 에러가 발생했습니다.')
    }
    this.onRefreshView().catch(console.error)
  }

  onReceiveAllItems = async () => {
    const { messageStore, authStore } = this.props
    const { receiveFailMessageDetail, errcode } = await messageStore.receiveMessage({ all: true })
    const { failMessageIds, failItems } = receiveFailMessageDetail

    for (const row of this.rowData) {
      if (row.receivedItem === 0 && row.itemCode > 0) {
        const foundFailMessageId = _.find(failMessageIds, id => id === row.id)
        if (_.isEmpty(foundFailMessageId)) {
          continue
        }
        row.receivedItem = 1
        row.receiverRead = 1
      }
    }

    if (!_.isEmpty(failItems)) {
      showReceiveFailItemsMessage(failItems)
      messageStore.setReceivedItemCount(_.size(failItems))
    } else if (errcode) {
      showReceiveItemErrorMessage(errcode)
    } else {
      messageStore.setReceivedItemCount(0)
      showToast(TOAST_TYPE.INFO, '아이템을 모두 받았습니다.')
      await authStore.refreshSimpleUserInfo(true)
    }
    this.onRefreshView().catch(console.error)
  }

  makeReceiveItemMessage = (row: Schema$Message): string => {
    if (row.item.volume_object === 1) {
      return `${row.item.name} x${row.itemVolume}을(를) 획득 하였습니다.`
    }
    return `${row.item.name}을(를) 획득 하였습니다.`
  }

  onClickDeleteAllButton = () => {
    this.setVisibleDelete(true)
  }

  onUserBlockSuccessHandler = (userPromiseId: string, blockTypes: COMMUNICATION_BLOCK_TYPE[]) => {
    const isUserBlock = !_.isEmpty(blockTypes)
    _.forEach(this.rowData, data => {
      if (data.senderId !== userPromiseId) {
        return
      }

      data.isBlocked = isUserBlock
    })

    this.onRefreshView()
  }

  renderMessageListFooter = () => {
    const { isLoading } = this.state
    const { currentSendReceiveTabKey, clubId } = this.props
    const { rowData } = this
    let footer = null
    if (isLoading) {
      footer = <PageLoading/>
    } else if (rowData.length > 0) {
      footer = <Fragment/>
    } else if (!clubId && currentSendReceiveTabKey === MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY.RECEIVE) {
      footer = <AdvEmpty ico={EMPTY_ICON.MESSAGE_RECV}/>
    } else if (!clubId && currentSendReceiveTabKey === MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY.SENT) {
      footer = <AdvEmpty ico={EMPTY_ICON.MESSAGE_SENT}/>
    } else if (!!clubId && currentSendReceiveTabKey === MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY.RECEIVE) {
      footer = <AdvEmpty ico={EMPTY_ICON.CLUB_MESSAGE_RECV}/>
    } else if (!!clubId && currentSendReceiveTabKey === MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY.SENT) {
      footer = <AdvEmpty ico={EMPTY_ICON.CLUB_MESSAGE_SENT}/>
    }
    return <div css={{ textAlign: 'center' }}>{footer}</div>
  }

  renderMessageListRow = rowData => {
    const { currentSendReceiveTabKey } = this.props
    return (
      <MessageListItem
        index={currentSendReceiveTabKey}
        track={rowData}
        messageType={this.messageTypeText}
        onDelete={this.onDeleteMessage}
        onReceiveItem={this.onReceiveItem}
        onReadMessage={this.onReadMessage}
        onSaveBettingHistoryShare={this.onSaveBettingHistoryShare}
        onUserBlockSuccessHandler={
        this.onUserBlockSuccessHandler
        //() => { this.fetch(true).catch(console.error) }
        }
      />
    )
  }

  getMessageTypeText(currentMessageTypeTabKey: MESSAGE_TYPE_TAB_INDEX_KEY): string {
    return currentMessageTypeTabKey === MESSAGE_TYPE_TAB_INDEX_KEY.MESSAGE ? '메시지' : '쪽지'
  }

  render() {
    const { dataSource, visibleDelete } = this.state
    const { currentMessageTypeTabKey, currentSendReceiveTabKey, clubId } = this.props

    this.messageTypeText = this.getMessageTypeText(currentMessageTypeTabKey)

    const { lastMessageTypeTabKey, lastIndex, lastClubId } = this
    this.lastMessageTypeTabKey = currentMessageTypeTabKey
    this.lastIndex = currentSendReceiveTabKey

    let needFetch = false

    if (clubId !== lastClubId) {
      this.lastClubId = clubId
      needFetch = true
    }

    if (currentMessageTypeTabKey !== lastMessageTypeTabKey) {
      this.lastMessageTypeTabKey = currentMessageTypeTabKey
      needFetch = true
    }

    if (currentSendReceiveTabKey !== lastIndex) {
      this.lastIndex = currentSendReceiveTabKey
      needFetch = true
    }

    if (this.mounted === false) {
      return getEmptyPage(true)
    }

    if (needFetch) {
      this.fetch(true).catch(console.error)
    }

    // TODO: 복수개의 ListView 의 페이지 끝 도달 이벤트가 처리되어 아래와 같이 예외 처리 추가! 근본적 해결책은?
    const endReachedHandler = this.onEndReached

    return (
      <ModalContents>
        <MessageListWrap>
          {/*{header}*/}
          <ListView
            // ref={el => (this.lv = el)}
            css={{ height: 'calc(100% - 118px)' }}
            dataSource={dataSource}
            renderFooter={this.renderMessageListFooter}
            renderRow={this.renderMessageListRow}
            className="am-list message-list"
            pageSize={PAGE_SIZE}
            // useBodyScroll
            renderBodyComponent={() => <MyBody/>}
            scrollRenderAheadDistance={500}
            onEndReached={endReachedHandler}
            onEndReachedThreshold={10}
          />
          {/* 전체 삭제 팝업 */}
          <AllDeletePopup
            visible={visibleDelete}
            setVisible={this.setVisibleDelete}
            onDelete={this.onDeleteAllMessage}
            messageType={this.messageTypeText}
          />
        </MessageListWrap>
      </ModalContents>
    )
  }
}

interface WrapperProps {
  currentMessageTypeTabKey: MESSAGE_TYPE_TAB_INDEX_KEY
  currentSendReceiveTabKey: MESSAGE_SEND_RECEIVE_TAB_INDEX_KEY
  dir: MESSAGE_DIR
  clubId?: string
  messageListRef?: React.MutableRefObject<MessageListRef>
}

const Wrapper: React.FC<WrapperProps> = (props: WrapperProps) => {
  const { messageStore, authStore, globalStore, routerStore, componentStore, logStore } = useRootStore()
  const { currentMessageTypeTabKey, currentSendReceiveTabKey, dir, clubId, messageListRef } = props
  const listProps = {
    currentMessageTypeTabKey,
    currentSendReceiveTabKey,
    dir,
    clubId,
    fetchMessageList: messageStore.fetchMessageList,
    messageStore,
    componentStore,
    authStore,
    globalStore,
    routerStore,
    logStore,
    messageListRef,
  }

  return <MessageList {...listProps} />
}

export default Wrapper
